import { computed, toRefs } from 'vue'
import { useInfiniteQuery } from '@tanstack/vue-query'

import { useI18n } from '@/composable/useI18n'
import api from '@/config/axios'
import { PROJECT_PAGES_GET_ALL } from '@/config/endpoints'
import { Page } from '@/store/interfaces'
import { useSelector } from '@/store/rootStore'
import { useNotification } from '../useNotification'

export type GetPagesByProjectIdResponse = Array<
	Page & {
		currency: string
	}
>
export type UseGetPagesByProjectId = {
	projectId: string
	limit: number
	enabled: boolean
}
export type UseGetPagesByIndexProjectId = UseGetPagesByProjectId & {
	indexMap: number[]
}

function sortPagesByIndex<T extends { index: number }>(pages: T[]): T[] {
	return [...pages].sort((a, b) => (a.index || 0) - (b.index || 0))
}

export function useGetPagesByProjectId(props: UseGetPagesByProjectId) {
	const { projectId, limit, enabled } = toRefs(props)
	const { endpoint } = PROJECT_PAGES_GET_ALL
	const { t } = useI18n()
	const { enqueueNotification } = useNotification()
	const pages = useSelector((state) => state.singleSetup.data?.project_pages)

	const sortedPages =
		pages.value && Array.isArray(pages.value) ? sortPagesByIndex(pages.value) : []

	return useInfiniteQuery({
		queryKey: computed(() => ['pages', projectId.value]),
		initialData: sortedPages
			? { pages: [sortedPages], pageParams: [0] }
			: undefined,
		queryFn: async (queryContext) => {
			const batchNumber = queryContext.pageParam ?? 0
			const startIndex = batchNumber * limit.value + 1
			const pageNumbers = Array.from(
				Array(limit.value),
				(_, idx) => startIndex + idx
			)

			const response = await api.get<GetPagesByProjectIdResponse | undefined>(
				endpoint(encodeURIComponent(projectId.value)),
				{
					params: {
						index: pageNumbers.join(','),
						sort: 'index:asc',
					},
				}
			)
			return response.data ? sortPagesByIndex(response.data) : []
		},
		getNextPageParam: (lastPage, allPages) => {
			if (!lastPage || lastPage.length < limit.value) return false
			return allPages.length ?? 0
		},
		staleTime: Infinity,
		refetchOnMount: false,
		enabled: computed(() => Boolean(projectId.value && enabled.value)),
		retry: (_, error) => {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			if (error.response?.status !== 423) {
				enqueueNotification({
					color: 'danger',
					message: `${t('common.SOMETHING_WENT_WRONG')}. ${t(
						'common.TRY_REFRESHING_OR_CONTACT_SUPPORT'
					)}`,
					closable: false,
					delay: 0,
				})
				return false
			}
			return true
		},
		retryDelay: (attemptIndex: number) =>
			Math.min(1000 * 2 ** attemptIndex, 30000),
	})
}

export function useGetPagesByIndexProjectId(
	props: UseGetPagesByIndexProjectId
) {
	const { projectId, limit, enabled } = toRefs(props)
	const { endpoint } = PROJECT_PAGES_GET_ALL
	const { enqueueNotification } = useNotification()
	const { t } = useI18n()

	return useInfiniteQuery({
		queryKey: computed(() => ['pagesIndex', projectId.value]),
		queryFn: async (queryContext) => {
			const batchNumber = queryContext.pageParam ?? 0
			const pageNumbers = props.indexMap.slice(
				batchNumber * limit.value,
				(batchNumber + 1) * limit.value
			)

			const response = await api.get<GetPagesByProjectIdResponse | undefined>(
				endpoint(encodeURIComponent(projectId.value)),
				{
					params: {
						index: pageNumbers.join(','),
						sort: 'index:asc',
					},
				}
			)
			return response.data ? sortPagesByIndex(response.data) : []
		},
		getNextPageParam: (lastPage, allPages) => {
			if (!lastPage || lastPage.length < limit.value) return false
			return allPages.length ?? 0
		},
		staleTime: Infinity,
		refetchOnMount: false,
		enabled: computed(() => Boolean(projectId.value && enabled.value)),
		retry: (_, error) => {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			if (error.response?.status !== 423) {
				enqueueNotification({
					color: 'danger',
					message: `${t('common.SOMETHING_WENT_WRONG')}. ${t(
						'common.TRY_REFRESHING_OR_CONTACT_SUPPORT'
					)}`,
					closable: false,
					delay: 0,
				})
				return false
			}
			return true
		},
		retryDelay: (attemptIndex: number) =>
			Math.min(1000 * 2 ** attemptIndex, 30000),
	})
}
